import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { AddCircleOutlineOutlined, CheckOutlined, SensorsOutlined } from '@mui/icons-material'
import { Button, Grid, IconButton, Skeleton, Typography } from '@mui/material'

import ActivateWithAppDialog from '../../components/activate-with-app-dialog'
import ButtonGroup from '../../components/m3/button-group'
import DashboardScreen from '../../components/screen/dashboard-screen'
import SelectedFolderBanner from '../../components/selected-folder-banner'
import StickyHeader from '../../components/sticky-header'
import sensorIcon from '../../images/sensor-icon.svg'
import { AuthenticationContext } from '../../providers/auth-provider'
import { DashboardContext } from '../../providers/dashboard-provider'
import Api from '../../utils/api'
import useIsMobile from '../../utils/hooks/useIsMobile'
import getNodeTypeMatrix from '../../utils/nodetype-matrix'

import AccordionGroupAttention, { IAttentionGroup } from './components/accordion-group-attention'
import AccordionGroupBuilding from './components/accordion-group-building'
import AccordionGroupExtraSensors, { EXTRA_SENSORS_GROUP_KEY } from './components/accordion-group-extra-sensors'
import AccordionGroupFloor from './components/accordion-group-floor'
import BookmarkDrawer from './components/bookmark-drawer'
import DeviceItem from './components/device-item'
import { getDevicePriority, getGroupSlug, groupDevices } from './helpers'
import useGroupHashing from './hooks'
import useStyles from './index.styles'

const Dashboard = () => {
  const classes = useStyles
  const isMobile = useIsMobile()

  const { ownUser } = useContext(AuthenticationContext)
  const { selectedFolderId, folder, activeAlerts, loading: dashboardLoading, error: dashboardError, refreshDevicesCounter } = useContext(DashboardContext)
  const navigate = useNavigate()
  const location = useLocation()

  const [activeDevices, setActiveDevices] = useState<IDeviceType[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>('')

  const [deviceGroups, setDeviceGroups] = useState<IBuildingGroup[]>([])
  const [ungroupedDevices, setUngroupedDevices] = useState<IDeviceType[]>([])
  const [expandedGroups, setExpandedGroups] = useState<string[]>([])
  const [expandedFloors, setExpandedFloors] = useState<string[]>([])
  const [expandedAttentionGroup, setExpandedAttentionGroup] = useState<string | false>(false)

  const [bookmarkingOpen, setBookmarkingOpen] = useState<boolean>(location.state?.showBookmarkInfo || false)
  const [activateWithAppDialogOpen, setActivateWithAppDialogOpen] = useState<boolean>(false)

  const [canActivateDevicesInWebApp, setCanActivateDevicesInWebApp] = useState<boolean>(false)
  const [canActivateDevicesInMobileApp, setCanActivateDevicesInMobileApp] = useState<boolean>(false)

  const [groupingType, setGroupingType] = useState<'all' | 'needsAttention'>('all')

  const devicesNeedAttention = useMemo(
    () => activeDevices.filter((device) => device.activeAlertsCount > 0 || device.isOffline || device.isExtra || device.hasLowSignal || device.hasLowBattery),
    [activeDevices]
  )
  const nonExtraActiveDevices = useMemo(() => activeDevices.filter((device) => !device.isExtra), [activeDevices])
  const nonExtraDevicesNeedAttention = useMemo(() => devicesNeedAttention.filter((device) => !device.isExtra), [devicesNeedAttention])
  const extraSensors = useMemo(() => activeDevices.filter((device) => device.isExtra), [activeDevices])
  const canShowActivationActions = canActivateDevicesInMobileApp || canActivateDevicesInWebApp

  const activeAlertsRef = useRef(activeAlerts)

  useEffect(() => {
    activeAlertsRef.current = activeAlerts
  }, [activeAlerts])

  // Reset state when switching folders to prevent showing stale data while loading new data
  useEffect(() => {
    setActiveDevices([])
    setDeviceGroups([])
    setUngroupedDevices([])
    setExpandedGroups([])
    setExpandedFloors([])
    setExpandedAttentionGroup(false)
    setCanActivateDevicesInWebApp(false)
    setCanActivateDevicesInMobileApp(false)
    setGroupingType('all')
  }, [selectedFolderId])

  useEffect(() => {
    if (deviceGroups.length === 0) return
    if (expandedGroups.length === 0) {
      const defaultExpandedGroups = deviceGroups.map((building) => building.name)
      if (extraSensors.length > 0) {
        defaultExpandedGroups.push(EXTRA_SENSORS_GROUP_KEY)
      }
      setExpandedGroups(defaultExpandedGroups)
    }
    if (expandedFloors.length === 0) {
      setExpandedFloors(deviceGroups.flatMap((building) => building.floors.map((floor) => `${building.name}||${floor.name}`)))
    }
  }, [deviceGroups, expandedFloors.length, expandedGroups.length, extraSensors.length])

  const getDevices = useCallback(async () => {
    try {
      if (selectedFolderId && !dashboardLoading) {
        setLoading(true)
        let useGrouping = false
        const deviceActivationCheckRes = await Api.get(`/api/v2/protect/folders/${selectedFolderId}/device-activation-check`)
        if (deviceActivationCheckRes.ok) {
          const devices: ICanActivateDeviceResponse[] = deviceActivationCheckRes.body?.devices ?? []
          const isLeakFreeze = (d: ICanActivateDeviceResponse) => d.nodeTypeName === 'leak5s' || d.nodeTypeName === 'nbiot_leak'

          const canActivateLeakFreezeDevices = devices.some((d) => isLeakFreeze(d) && d.canActivateMoreDevices)
          const canActivateOtherDevices = devices.some((d) => !isLeakFreeze(d) && d.canActivateMoreDevices)
          setCanActivateDevicesInWebApp(canActivateLeakFreezeDevices && !canActivateOtherDevices)
          setCanActivateDevicesInMobileApp(canActivateOtherDevices)

          const totalDevicesCount = devices.reduce((sum, d) => sum + d.totalDevicesNotInstalled + d.totalDevicesInstalled, 0)
          useGrouping = totalDevicesCount > 10
        }
        const res = await Api.get(`/api/v2/protect/folders/${selectedFolderId}/devices`)
        if (res.ok) {
          const result = res.body.devices.filter((device: IDeviceType) => device.node.isActive && (device.isActivated || device.isExtra))

          if (result.length > 0) {
            const nodeIds = result.map((device: IDeviceType) => device.node.id).join(`&nodeId=`)
            const channelsRes = await Api.get(`/api/data/current?nodeId=${nodeIds}`)

            if (channelsRes.ok) {
              const deviceAlerts = Object.groupBy?.(activeAlertsRef.current, ({ nodeId }) => nodeId)

              const newData = result
                .map((device: IDeviceType) => {
                  return { ...device, currentValues: channelsRes.body[device.node.id], alerts: deviceAlerts[device.node.id] ?? [] }
                })
                .sort((a: IDeviceType, b: IDeviceType) => {
                  const priorityDiff = getDevicePriority(a) - getDevicePriority(b)
                  if (priorityDiff !== 0) return priorityDiff
                  return getNodeTypeMatrix[a.node.nodeTypeName ?? ''].localeCompare(getNodeTypeMatrix[b.node.nodeTypeName ?? ''])
                })

              setActiveDevices(newData)

              if (newData.length > 0 && useGrouping) {
                const nonExtraDevices = newData.filter((d: IDeviceType) => !d.isExtra)
                const { buildingGroups, ungroupedDevices: devicesWithoutGrouping } = groupDevices({ newData: nonExtraDevices })
                setDeviceGroups(buildingGroups)
                setUngroupedDevices(devicesWithoutGrouping)
              } else {
                setDeviceGroups([])
                setUngroupedDevices([])
              }
            } else {
              setError('Error fetching channel data')
            }
          } else {
            // Reset the state if there are no devices to prevent showing stale data when switching between folders
            setActiveDevices([])
            setDeviceGroups([])
            setUngroupedDevices([])
            setExpandedGroups([])
            setExpandedFloors([])
          }
        } else if (res?.body?.message === 'folder must be of type Participant') {
          setError('Must be a Participant folder')
        } else {
          setError('Error fetching devices')
        }
        setLoading(false)
      }
    } catch {
      setError('Error fetching devices')
      setLoading(false)
    }
  }, [selectedFolderId, dashboardLoading])

  useEffect(() => {
    getDevices()
  }, [getDevices, refreshDevicesCounter])

  // Re-map alerts onto loaded devices when activeAlerts changes without refetching
  useEffect(() => {
    const deviceAlerts = Object.groupBy?.(activeAlerts, ({ nodeId }) => nodeId)
    setActiveDevices((prev) => {
      if (prev.length === 0) return prev
      return prev.map((device) => ({
        ...device,
        alerts: deviceAlerts?.[device.node.id] ?? [],
      }))
    })
  }, [activeAlerts])

  const attentionGroups: IAttentionGroup[] = useMemo(
    () => [
      { label: 'Alerting', key: 'alerting', devices: devicesNeedAttention.filter((d) => d.activeAlertsCount > 0 && !d.isExtra) },
      { label: 'Offline', key: 'offline', devices: devicesNeedAttention.filter((d) => d.isOffline && !d.isExtra) },
      { label: 'Low Battery', key: 'low-battery', devices: devicesNeedAttention.filter((d) => d.hasLowBattery && !d.isOffline && !d.isExtra) },
      { label: 'Poor Signal', key: 'poor-signal', devices: devicesNeedAttention.filter((d) => d.hasLowSignal && !d.isOffline && !d.isExtra) },
      { label: 'Extra Sensors', key: 'extra-sensors', devices: devicesNeedAttention.filter((d) => d.isExtra) },
    ],
    [devicesNeedAttention]
  )
  // Restore open accordion from URL hash when groups are loaded
  useGroupHashing({ setGroupingType, trigger: selectedFolderId })

  const makeMultiToggleHandler =
    (setter: React.Dispatch<React.SetStateAction<string[]>>) => (key: string) => (_event: React.SyntheticEvent, newExpanded: boolean) => {
      setter((prev) => {
        if (newExpanded) {
          return prev.includes(key) ? prev : [...prev, key]
        }

        return prev.filter((value) => value !== key)
      })
    }

  const makeSingleToggleHandler =
    (setter: React.Dispatch<React.SetStateAction<string | false>>) => (key: string) => (_event: React.SyntheticEvent, newExpanded: boolean) => {
      setter(newExpanded ? key : false)
    }

  const handleGroupToggle = makeMultiToggleHandler(setExpandedGroups)
  const handleFloorToggle = makeMultiToggleHandler(setExpandedFloors)
  const handleAttentionToggle = makeSingleToggleHandler(setExpandedAttentionGroup)

  const getDefaultExpandedGroups = () => {
    const groups = deviceGroups.map((building) => building.name)
    if (extraSensors.length > 0) {
      groups.push(EXTRA_SENSORS_GROUP_KEY)
    }
    return groups
  }

  const getDefaultExpandedFloors = () => deviceGroups.flatMap((building) => building.floors.map((floor) => `${building.name}||${floor.name}`))

  const handleAllDevicesClick = () => {
    navigate({ hash: '' }, { replace: true, state: location.state })
    setExpandedGroups(getDefaultExpandedGroups())
    setExpandedFloors(getDefaultExpandedFloors())
    setExpandedAttentionGroup(false)
    setGroupingType('all')
  }

  const handleNeedsAttentionClick = () => {
    navigate({ hash: getGroupSlug('needs-attention') }, { replace: true, state: location.state })
    setGroupingType('needsAttention')
  }

  const handleAddDeviceClick = () => {
    if (canActivateDevicesInWebApp) {
      navigate(`/device-roster/${selectedFolderId}`)
    } else {
      setActivateWithAppDialogOpen(true)
    }
  }

  const screenLoading = !!selectedFolderId && (loading || dashboardLoading)

  return (
    <DashboardScreen
      error={error || dashboardError}
      headerProps={{
        showLogo: true,
        rightIconMobile: canShowActivationActions ? (
          <IconButton size="small" edge="end" color="secondary" aria-label="Add Device" onClick={handleAddDeviceClick}>
            <AddCircleOutlineOutlined />
          </IconButton>
        ) : null,
      }}
      data-testid="dashboard-screen"
    >
      <Grid size={12} container={true}>
        {isMobile && <SelectedFolderBanner folder={folder} />}

        {ownUser && selectedFolderId && (
          <>
            <StickyHeader marginBottom={1}>
              <Grid container={true} size={12} sx={classes.devicesHeaderRow}>
                <Typography variant="body2" sx={classes.devicesHeader}>
                  <SensorsOutlined /> Devices {activeDevices.length > 0 ? activeDevices.length : ''}
                </Typography>

                {!isMobile && canShowActivationActions && (
                  <IconButton color="primary" onClick={handleAddDeviceClick} aria-label="Add Device">
                    <AddCircleOutlineOutlined />
                  </IconButton>
                )}
              </Grid>

              {activeDevices.length > 0 && (
                <ButtonGroup fullWidth={true} sx={classes.buttonGroup}>
                  <ButtonGroup.Button selected={groupingType === 'all'} icon={<CheckOutlined fontSize="small" />} onClick={handleAllDevicesClick}>
                    All Devices ({activeDevices.length})
                  </ButtonGroup.Button>
                  <ButtonGroup.Button selected={groupingType === 'needsAttention'} icon={undefined} onClick={handleNeedsAttentionClick}>
                    Needs Attention ({devicesNeedAttention.length})
                  </ButtonGroup.Button>
                </ButtonGroup>
              )}
            </StickyHeader>

            {screenLoading ? (
              <Grid container={true} size={12} sx={classes.skeletonGrid}>
                <Skeleton variant="rounded" width="100%" height={37} sx={classes.skeletonButtonGroup} />
                <Skeleton variant="rounded" width="100%" height={96} />
                <Skeleton variant="rounded" width="100%" height={96} />
                <Skeleton variant="rounded" width="100%" height={96} />
              </Grid>
            ) : (
              <>
                {activeDevices.length === 0 && (
                  <Grid container={true} size={12} sx={classes.emptyStateContainer}>
                    <Grid size={12} sx={classes.emptyStateImageContainer}>
                      <img src={sensorIcon} alt="Sensor" />
                    </Grid>

                    <Typography variant="body1">No sensors yet</Typography>

                    <Typography variant="body2" color="text.secondary" sx={classes.emptyStateDescription}>
                      Activate your sensors to receive alerts and stay protected.
                    </Typography>

                    <Button size="large" variant="contained" disableElevation onClick={handleAddDeviceClick}>
                      Start Activation
                    </Button>
                  </Grid>
                )}

                {deviceGroups.length > 0 && groupingType === 'all' && (
                  <>
                    {deviceGroups.map((building) => {
                      const buildingContent = building.floors.map((floor) => {
                        const floorKey = `${building.name}||${floor.name}`
                        return (
                          <AccordionGroupFloor
                            key={floorKey}
                            building={building}
                            floor={floor}
                            floorKey={floorKey}
                            isExpanded={expandedFloors.includes(floorKey)}
                            onToggle={handleFloorToggle}
                          />
                        )
                      })
                      return (
                        <AccordionGroupBuilding
                          key={building.name}
                          building={building}
                          buildingContent={buildingContent}
                          isExpanded={expandedGroups.includes(building.name)}
                          onToggle={handleGroupToggle}
                        />
                      )
                    })}

                    {ungroupedDevices.length > 0 && (
                      <Grid container={true} columnSpacing={1} rowSpacing={1} size={12}>
                        {ungroupedDevices.map((device) => {
                          return <DeviceItem key={device.node.id} device={device} />
                        })}
                      </Grid>
                    )}

                    <AccordionGroupExtraSensors
                      devices={extraSensors}
                      isExpanded={expandedGroups.includes(EXTRA_SENSORS_GROUP_KEY)}
                      onToggle={handleGroupToggle}
                      showActivateButton={canShowActivationActions}
                      onActivate={handleAddDeviceClick}
                    />
                  </>
                )}

                {deviceGroups.length > 0 && groupingType === 'needsAttention' && (
                  <AccordionGroupAttention
                    groups={attentionGroups}
                    expandedGroup={expandedAttentionGroup}
                    onToggle={handleAttentionToggle}
                    showActivateButton={canShowActivationActions}
                    onActivate={handleAddDeviceClick}
                  />
                )}

                {deviceGroups.length === 0 && activeDevices.length > 0 && groupingType === 'all' && (
                  <>
                    {nonExtraActiveDevices.length > 0 && (
                      <Grid container={true} columnSpacing={1} rowSpacing={1} size={12}>
                        {nonExtraActiveDevices.map((device) => {
                          return <DeviceItem key={device.node.id} device={device} />
                        })}
                      </Grid>
                    )}
                    <AccordionGroupExtraSensors
                      devices={extraSensors}
                      isExpanded={expandedGroups.includes(EXTRA_SENSORS_GROUP_KEY)}
                      onToggle={handleGroupToggle}
                      showActivateButton={canShowActivationActions}
                      onActivate={handleAddDeviceClick}
                      displayMode="panel"
                    />
                  </>
                )}

                {deviceGroups.length === 0 && groupingType === 'needsAttention' && devicesNeedAttention.length > 0 && (
                  <>
                    {nonExtraDevicesNeedAttention.length > 0 && (
                      <Grid container={true} columnSpacing={1} rowSpacing={1} size={12}>
                        {nonExtraDevicesNeedAttention.map((device) => {
                          return <DeviceItem key={device.node.id} device={device} />
                        })}
                      </Grid>
                    )}
                    <AccordionGroupExtraSensors
                      devices={extraSensors}
                      isExpanded={expandedAttentionGroup === EXTRA_SENSORS_GROUP_KEY}
                      onToggle={handleAttentionToggle}
                      showActivateButton={canShowActivationActions}
                      onActivate={handleAddDeviceClick}
                      displayMode="panel"
                    />
                  </>
                )}
              </>
            )}
          </>
        )}

        <BookmarkDrawer bookmarkingOpen={bookmarkingOpen} setBookmarkingOpen={setBookmarkingOpen} selectedFolderId={selectedFolderId} />
        <ActivateWithAppDialog open={activateWithAppDialogOpen} onClose={() => setActivateWithAppDialogOpen(false)} />
      </Grid>
    </DashboardScreen>
  )
}

export default Dashboard
